// The platform console's calls into `/v1/platform/*` (spec 15 §5), through the
// ONE generated client (doctrine 3 — no hand-rolled fetch anywhere in this
// area). The session is an opaque `__Host-` cookie the browser carries by
// itself; nothing here reads, writes or stores a credential.
//
// WHY NOT `lib/request.ts#unwrap`: that helper turns EVERY 401 into
// `UnauthorizedError` so a screen can bounce to /login. In this area a 401 is
// usually a normal answer — wrong password, wrong TOTP code, dead invite token
// — and bouncing on it would replace the honest message with a redirect loop.
// So the failures arrive as a `PlatformError` carrying the status and the
// stable problem `code`, and each screen decides what that means.

import { client } from "../../../lib/api";
import type { components } from "@assessly/api-client";

type Problem = components["schemas"]["Problem"];

export type PlatformRole = components["schemas"]["PlatformMeResponse"]["role"];
/** fleet, or sandbox: a sandbox member writes about sandbox tenants only (D-477). */
export type PlatformScope = components["schemas"]["PlatformMeResponse"]["scope"];

export interface PlatformMe {
  id: string;
  email: string;
  /** M15-9: the member's own name. Absent from a probe answered before it. */
  display_name?: string;
  role: PlatformRole;
  totp_confirmed: boolean;
  scope: PlatformScope;
  /** Unused recovery codes (spec 15 §3.1 step 4); null while enrolling. */
  recovery_codes_remaining?: number | null;
}

/** A non-2xx answer from `/v1/platform/*`, with the fields the screens branch on. */
export class PlatformError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly requestId: string | undefined;
  /** The problem's `reason` extension, where a refusal names one (M15-8's
   * SUPPORT_RESET_NOT_ELIGIBLE, the break-glass refusal). */
  readonly reason: string | undefined;
  /** Whole seconds from `Retry-After`, where a 429 or 423 named one. */
  readonly retryAfterSeconds: number | undefined;

  constructor(
    status: number,
    problem: Problem | undefined,
    fallback: string,
    retryAfterSeconds?: number,
  ) {
    super(problem?.title ?? fallback);
    this.name = "PlatformError";
    this.status = status;
    this.code = problem?.code;
    this.requestId = problem?.request_id;
    const reason = (problem as Record<string, unknown> | undefined)?.["reason"];
    this.reason = typeof reason === "string" ? reason : undefined;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** `Retry-After` as whole seconds. The server sends delta-seconds only, so a
 * date or anything else reads as absent rather than as a guess. */
export function retryAfterSeconds(response: Response): number | undefined {
  const raw = response.headers.get("Retry-After");
  if (raw === null || !/^\d+$/u.test(raw.trim())) return undefined;
  const n = Number(raw.trim());
  return Number.isSafeInteger(n) && n > 0 ? n : undefined;
}

export function isProblem(value: unknown): value is Problem {
  return typeof value === "object" && value !== null && "title" in value && "status" in value;
}

export function unwrap<T>(
  result: { data?: T | undefined; error?: unknown; response: Response },
  fallback: string,
): T {
  if (result.error !== undefined || result.data === undefined) {
    throw new PlatformError(
      result.response.status,
      isProblem(result.error) ? result.error : undefined,
      fallback,
      retryAfterSeconds(result.response),
    );
  }
  return result.data;
}

/** §5 — the SPA guard's session probe. Answers for a `totp_pending` staff too. */
export async function fetchPlatformMe(signal?: AbortSignal): Promise<PlatformMe> {
  const result = await client.GET("/v1/platform/me", signal ? { signal } : {});
  return unwrap(result, "session probe failed");
}

/** §3.1.2 — validate the invite token and set the password. */
export async function acceptInvite(input: { token: string; password: string }): Promise<void> {
  const result = await client.POST("/v1/platform/auth/accept-invite", { body: input });
  unwrap(result, "accept-invite failed");
}

/**
 * §3.1 step 7a (D-587) — ask for a reset link. The server answers 202 with no
 * body for every address, so a resolved promise says only that the request
 * was taken, never that an account exists.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const result = await client.POST("/v1/platform/auth/password-reset", { body: { email } });
  if (result.error !== undefined || !result.response.ok) {
    throw new PlatformError(
      result.response.status,
      isProblem(result.error) ? result.error : undefined,
      "reset request failed",
    );
  }
}

/** §3.1 step 7b — set the new password from the emailed link. No session. */
export async function completePasswordReset(input: {
  token: string;
  new_password: string;
}): Promise<void> {
  const result = await client.POST("/v1/platform/auth/password-reset/complete", { body: input });
  unwrap(result, "password reset failed");
}

export type LoginStep = components["schemas"]["PlatformLoginResponse"];

/** §3.1.4 password step: a login ticket (active) or a restricted session (pending). */
export async function platformLogin(input: {
  email: string;
  password: string;
}): Promise<LoginStep> {
  const result = await client.POST("/v1/platform/auth/login", { body: input });
  return unwrap(result, "sign-in failed");
}

/** §3.1.4 TOTP step — a 6-digit code OR an unused recovery code. Mints the session. */
export async function platformTotpStep(input: {
  login_ticket: string;
  code?: string;
  recovery_code?: string;
}): Promise<void> {
  const result = await client.POST("/v1/platform/auth/totp", { body: input });
  unwrap(result, "two-factor step failed");
}

export interface TotpEnrollment {
  otpauth_uri: string;
  secret: string;
}

/** §3.1.3 step 1 — generate the secret. Restricted (totp_pending) session only. */
export async function enrollTotp(): Promise<TotpEnrollment> {
  const result = await client.POST("/v1/platform/auth/totp/enroll", {});
  return unwrap(result, "could not start enrollment");
}

/** §3.1.3 step 2 — confirm; returns the ten recovery codes, once and only once. */
export async function confirmTotp(code: string): Promise<readonly string[]> {
  const result = await client.POST("/v1/platform/auth/totp/confirm", { body: { code } });
  return unwrap(result, "could not confirm the code").recovery_codes;
}

/**
 * §5 — reissue the ten recovery codes, voiding every unused one. Needs a FRESH
 * TOTP code (the endpoint verifies it against the enrolled secret), which is
 * what stops a borrowed session from silently rotating the owner out of their
 * own account. Active staff only: a `totp_pending` session is refused.
 */
export async function regenerateRecoveryCodes(code: string): Promise<readonly string[]> {
  const result = await client.POST("/v1/platform/auth/recovery-codes/regenerate", {
    body: { code },
  });
  return unwrap(result, "could not reissue the recovery codes").recovery_codes;
}

/**
 * What a failed TOTP-checking write actually said about the six digits the
 * operator typed — for `POST auth/totp/confirm` and
 * `POST auth/recovery-codes/regenerate`, the two writes whose failure banners
 * talk about "your code".
 *
 * WHY THIS IS CENTRALISED (probed 2026-08-17, the unknown-bad-broken audit):
 * both screens collapsed EVERY failure — a TypeError('Failed to fetch') that
 * never left the browser, a 500, an expired session — into "That code was not
 * accepted. Wait for the next one and try again." On the enrolment screen that
 * misdiagnosis is destructive, not merely wrong: its own header records that a
 * second `POST auth/totp/enroll` mints a FRESH secret and permanently
 * invalidates the QR the user has already scanned, so an operator told their
 * code was refused when the network blipped is being pushed toward the retry
 * that destroys a working enrolment. login.tsx already drew this exact line in
 * its verify onError; this helper IS that line, shared, so the two remaining
 * TOTP-checking writes cannot drift back to the collapse.
 *
 *   rejected        — the server compared the code to the enrolled secret and
 *                     said no (401 INVALID_TOTP_CODE). The ONLY answer the
 *                     no-oracle collapsed sentence may be worn by: wrong, stale
 *                     and replayed still read identically on the glass, because
 *                     telling them apart describes the TOTP window to a guesser.
 *   shape           — the contract refused the request before the service ran
 *                     (400/422). A statement about the FORM of the input, never
 *                     about the secret; the submit gates on the same shapes, so
 *                     this is the belt to the browser's braces.
 *   session_expired — a 401 that is NOT the TOTP verdict: the platform session
 *                     guard threw before the service ever saw the code
 *                     (NOT_AUTHENTICATED — idle timeout, the 12-hour ceiling,
 *                     or a sign-out elsewhere). Nothing judged the code; the
 *                     only working door is sign-in, and the screen must point
 *                     at it rather than at the authenticator app.
 *   broken          — nothing was judged and retrying the SAME code is safe: a
 *                     transport failure (fetch rejects with a TypeError before
 *                     any response exists, so there is no PlatformError to
 *                     instance-check), a 5xx, or an answer this client does not
 *                     recognise. One kind for all of them, deliberately — they
 *                     are one fact ("nobody judged anything, try again"), the
 *                     same fold unknown-bad-broken.test.tsx pins on the sign-in
 *                     screen. Carries the request id when the server answered
 *                     with one, so the operator can quote it at support.
 */
export type TotpCheckFailure =
  | { kind: "rejected" }
  | { kind: "shape" }
  | { kind: "session_expired" }
  | { kind: "broken"; requestId: string | undefined };

export function classifyTotpCheckFailure(error: unknown): TotpCheckFailure {
  if (!(error instanceof PlatformError)) return { kind: "broken", requestId: undefined };
  if (error.status === 400 || error.status === 422) return { kind: "shape" };
  if (error.status === 401) {
    // The 401 splits on the problem code, and the split is the whole point:
    // INVALID_TOTP_CODE is the service's verdict on the digits; every other
    // 401 flavour (the guard's NOT_AUTHENTICATED, or a bodyless 401 from the
    // infrastructure in front of the API) means the session, not the code —
    // require-session.tsx applies the same reading to the probe: "401 is the
    // ANSWER 'you have no session'".
    return error.code === "INVALID_TOTP_CODE" ? { kind: "rejected" } : { kind: "session_expired" };
  }
  return { kind: "broken", requestId: error.requestId };
}

/** §5 — revoke the current staff session. */
export async function platformLogout(): Promise<void> {
  const result = await client.POST("/v1/platform/auth/logout", {});
  if (!result.response.ok) {
    throw new PlatformError(
      result.response.status,
      isProblem(result.error) ? result.error : undefined,
      "sign-out failed",
    );
  }
}

// ---- tenants (spec 15 §4 S4, S5, S11) -------------------------------------
//
// Every mutation carries an Idempotency-Key (spec 15 §5) that the CALLER owns.
// A write whose answer was lost may still have committed, so a retry of the
// same body must send the same key and get the stored answer back rather than
// run twice. The screens hold one key per body until the server has answered
// it (lib/held-key.ts).

export type TenantSummary = components["schemas"]["PlatformTenantSummary"];
export type TenantDetail = components["schemas"]["PlatformTenantDetail"];
export type TenantPage = components["schemas"]["PlatformTenantListResponse"];
export type LifecycleEventsPage = components["schemas"]["LifecycleEventsResponse"];
export type LifecycleEvent = LifecycleEventsPage["items"][number];
export type SubdomainCheck = components["schemas"]["PlatformSubdomainCheckResponse"];
export type VerticalPack = components["schemas"]["PlatformVerticalPack"];
export type CreateTenantInput = components["schemas"]["PlatformCreateTenantRequest"];
export type SuspendInput = components["schemas"]["PlatformSuspendRequest"];
export type RestoreInput = components["schemas"]["PlatformRestoreRequest"];
export type PlatformRegion = TenantSummary["region"];
export type PlatformStaffMember = components["schemas"]["PlatformStaffView"];

/** Platform routes take the header without declaring it. */
export function idempotent(key: string): Record<string, string> {
  return { "Idempotency-Key": key };
}

/** One page of tenants, newest first. `cursor` is the previous page's `next_cursor`. */
export async function listTenants(
  query: { status?: string; q?: string; cursor?: string; limit?: number },
  signal?: AbortSignal,
): Promise<TenantPage> {
  const result = await client.GET("/v1/platform/tenants", {
    params: { query },
    ...(signal ? { signal } : {}),
  });
  return unwrap(result, "could not load tenants");
}

export async function getTenant(id: string, signal?: AbortSignal): Promise<TenantDetail> {
  const result = await client.GET("/v1/platform/tenants/{id}", {
    params: { path: { id } },
    ...(signal ? { signal } : {}),
  });
  return unwrap(result, "could not load the tenant");
}

export async function listLifecycleEvents(
  id: string,
  cursor: string | undefined,
  signal?: AbortSignal,
): Promise<LifecycleEventsPage> {
  const result = await client.GET("/v1/platform/tenants/{id}/lifecycle-events", {
    params: { path: { id }, query: cursor === undefined ? {} : { cursor } },
    ...(signal ? { signal } : {}),
  });
  return unwrap(result, "could not load the tenant's history");
}

/** The S11 wizard's live check. Advice only: the create still decides. */
export async function checkSubdomain(
  subdomain: string,
  signal?: AbortSignal,
): Promise<SubdomainCheck> {
  const result = await client.GET("/v1/platform/subdomains/{subdomain}/availability", {
    params: { path: { subdomain } },
    ...(signal ? { signal } : {}),
  });
  return unwrap(result, "could not check the subdomain");
}

export async function listVerticalPacks(signal?: AbortSignal): Promise<readonly VerticalPack[]> {
  const result = await client.GET("/v1/platform/vertical-packs", signal ? { signal } : {});
  return unwrap(result, "could not load the vertical packs").items;
}

/** Staff directory, for naming who did what on the timeline. admin + readonly only. */
export async function listStaff(signal?: AbortSignal): Promise<readonly PlatformStaffMember[]> {
  const result = await client.GET("/v1/platform/staff", signal ? { signal } : {});
  return unwrap(result, "could not load staff").items;
}

export async function createTenant(input: CreateTenantInput, key: string): Promise<TenantDetail> {
  const result = await client.POST("/v1/platform/tenants", {
    body: input,
    headers: idempotent(key),
  });
  return unwrap(result, "could not create the tenant");
}

export async function suspendTenant(
  id: string,
  input: SuspendInput,
  key: string,
): Promise<TenantDetail> {
  const result = await client.POST("/v1/platform/tenants/{id}/suspend", {
    params: { path: { id } },
    headers: idempotent(key),
    body: input,
  });
  return unwrap(result, "could not suspend the tenant");
}

export async function restoreTenant(
  id: string,
  input: RestoreInput,
  key: string,
): Promise<TenantDetail> {
  const result = await client.POST("/v1/platform/tenants/{id}/restore", {
    params: { path: { id } },
    headers: idempotent(key),
    body: input,
  });
  return unwrap(result, "could not restore the tenant");
}

export async function retryProvisioning(id: string, key: string): Promise<TenantDetail> {
  const result = await client.POST("/v1/platform/tenants/{id}/retry-provisioning", {
    params: { path: { id } },
    headers: idempotent(key),
  });
  return unwrap(result, "could not retry provisioning");
}

export async function resendAdminInvite(id: string, key: string): Promise<TenantDetail> {
  const result = await client.POST("/v1/platform/tenants/{id}/resend-admin-invite", {
    params: { path: { id } },
    headers: idempotent(key),
    body: {},
  });
  return unwrap(result, "could not resend the invite");
}

// ---- S3, the fleet overview (spec 15 §4; D-625) ---------------------------

export type FleetOverview = components["schemas"]["PlatformFleetOverviewResponse"];
export type FleetTile = FleetOverview["tiles"][number];
export type FleetAttentionItem = FleetOverview["attention"][number];
export type FleetEvent = FleetOverview["recent_events"][number];

/** The home's one read: tiles, tenants needing attention, recent changes. */
export async function fetchFleetOverview(signal?: AbortSignal): Promise<FleetOverview> {
  const result = await client.GET("/v1/platform/fleet/overview", signal ? { signal } : {});
  return unwrap(result, "could not load the fleet overview");
}

// ---- tenant health and logs (spec 15 §4 S9, S15; M15-4, D-611 to D-613) -----

export type HealthBoard = components["schemas"]["PlatformHealthBoardResponse"];
export type HealthBoardRow = HealthBoard["items"][number];
export type TenantHealth = components["schemas"]["PlatformTenantHealthResponse"];
export type HealthSnapshot = NonNullable<components["schemas"]["PlatformHealthSnapshot"]>;
export type HealthDay = components["schemas"]["PlatformHealthDay"];
export type HealthAlert = components["schemas"]["PlatformHealthAlert"];
export type HealthSummary = components["schemas"]["PlatformTenantHealthSummary"];
export type FleetBackup = NonNullable<components["schemas"]["PlatformFleetBackup"]>;
export type RequestErrorPage = components["schemas"]["PlatformRequestErrorListResponse"];
export type RequestErrorRow = RequestErrorPage["items"][number];
export type AlertPage = components["schemas"]["PlatformHealthAlertListResponse"];
export type LogSince = "1h" | "24h" | "7d";
export type HealthSeriesWindow = "24h" | "7d" | "30d";

/** S15 — every active tenant, worst first. */
export async function getHealthBoard(signal?: AbortSignal): Promise<HealthBoard> {
  const result = await client.GET("/v1/platform/health", signal ? { signal } : {});
  return unwrap(result, "could not load the health board");
}

/** S9 — one tenant's windows (24h) or rolled-up days (7d, 30d). */
export async function getTenantHealth(
  id: string,
  window: HealthSeriesWindow,
  signal?: AbortSignal,
): Promise<TenantHealth> {
  const result = await client.GET("/v1/platform/tenants/{id}/health", {
    params: { path: { id }, query: { window } },
    ...(signal ? { signal } : {}),
  });
  return unwrap(result, "could not load the tenant's health");
}

/** D-612 — start or stop a health drill on a sandbox tenant (platform_admin). */
export async function setHealthDrill(
  id: string,
  healthDrill: boolean,
  key: string,
): Promise<TenantDetail> {
  const result = await client.PUT("/v1/platform/tenants/{id}/health-drill", {
    params: { path: { id } },
    headers: idempotent(key),
    body: { health_drill: healthDrill },
  });
  return unwrap(result, "could not change the health drill");
}

/** D-613 — the server errors the request meter kept, newest first. */
export async function listLogErrors(
  query: { tenant_id?: string; since?: LogSince; cursor?: string; limit?: number },
  signal?: AbortSignal,
): Promise<RequestErrorPage> {
  const result = await client.GET("/v1/platform/logs/errors", {
    params: { query },
    ...(signal ? { signal } : {}),
  });
  return unwrap(result, "could not load the server errors");
}

/** D-613 — health alerts, open or all, newest first. */
export async function listLogAlerts(
  query: { tenant_id?: string; state?: "open" | "all"; cursor?: string; limit?: number },
  signal?: AbortSignal,
): Promise<AlertPage> {
  const result = await client.GET("/v1/platform/logs/alerts", {
    params: { query },
    ...(signal ? { signal } : {}),
  });
  return unwrap(result, "could not load the alerts");
}
